import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useReopeningWindow } from "@/context/ConfigContext";
import { toast } from "sonner";

export default function WaitlistForm({ interestedIn = "counselling", tone = "light" }) {
  const { t, lang } = useLang();
  const reopening = useReopeningWindow();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const dark = tone === "dark";
  const inputStyle = dark
    ? { background: "rgba(255,255,255,0.1)", color: "#FBF9F5", border: "1px solid rgba(251, 249, 245, 0.3)" }
    : { background: "var(--surface)", color: "var(--ink)", border: "1px solid var(--border-medium)" };

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await api.post("/practice/waitlist", { ...form, interested_in: interestedIn, lang });
      setSubmitted(true);
      toast.success(t({ en: "You're on the list.", fr: "Vous êtes sur la liste." }));
    } catch (e) {
      toast.error(e.response?.data?.detail || t({ en: "Something went wrong.", fr: "Une erreur est survenue." }));
    } finally { setSubmitting(false); }
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (submitted) {
    const window = t({ en: reopening.en, fr: reopening.fr });
    return (
      <div className="p-6 rounded-lg" style={{ background: dark ? "rgba(255,255,255,0.1)" : "var(--surface-muted)" }} data-testid="waitlist-success">
        <CheckCircle2 size={32} className="mb-3" style={{ color: dark ? "#FBF9F5" : "var(--brand)" }} />
        <div className="font-serif text-xl mb-2" style={{ color: dark ? "#FBF9F5" : "var(--ink)" }}>{t({ en: "You're on the list.", fr: "Vous êtes sur la liste." })}</div>
        <p className="text-sm mb-2" style={{ color: dark ? "rgba(251, 249, 245, 0.9)" : "var(--ink-2)" }}>
          {t({ en: `The practice is aiming to reopen in ${window}.`, fr: `La réouverture du cabinet est prévue au ${window}.` })}
        </p>
        <p className="text-sm" style={{ color: dark ? "rgba(251, 249, 245, 0.85)" : "var(--ink-2)" }}>
          {t({ en: "I'll email you personally when the calendar opens. A confirmation is on its way to your inbox.", fr: "Je vous écrirai personnellement dès l'ouverture du calendrier. Une confirmation est en route dans votre boîte de réception." })}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="practice-waitlist-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input required placeholder={t({ en: "Name", fr: "Nom" })} value={form.name} onChange={upd("name")} className="w-full p-3 rounded-lg" style={inputStyle} data-testid="waitlist-name" />
        <input required type="email" placeholder={t({ en: "Email", fr: "Courriel" })} value={form.email} onChange={upd("email")} className="w-full p-3 rounded-lg" style={inputStyle} data-testid="waitlist-email" />
      </div>
      <input placeholder={t({ en: "Phone (optional)", fr: "Téléphone (facultatif)" })} value={form.phone} onChange={upd("phone")} className="w-full p-3 rounded-lg" style={inputStyle} data-testid="waitlist-phone" />
      <textarea rows={2} placeholder={t({ en: "Anything you'd like me to know (optional)", fr: "Ce que vous aimeriez que je sache (facultatif)" })} value={form.message} onChange={upd("message")} className="w-full p-3 rounded-lg" style={inputStyle} data-testid="waitlist-message" />
      <button type="submit" disabled={submitting} className="btn-accent" data-testid="btn-waitlist-submit">
        {submitting ? t({ en: "Submitting…", fr: "Envoi…" }) : t({ en: "Join the waitlist", fr: "Rejoindre la liste" })}
      </button>
    </form>
  );
}
